import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Search, 
  ChevronDown, 
  ShoppingCart, 
  CreditCard, 
  Truck, 
  RotateCcw, 
  Paintbrush,
  HelpCircle,
  MessageCircle
} from 'lucide-react';

// All FAQ categories with their questions
const FAQ_CATEGORIES = [
  {
    id: 'acquisti',
    name: 'Acquisti',
    icon: ShoppingCart,
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    link: '/come-acquistare',
    questions: [
      {
        question: 'Quando viene addebitato il pagamento?',
        answer: 'Il pagamento viene addebitato immediatamente al momento del checkout, ma viene trattenuto da Stripe in modo sicuro. L\'artigiano riceve il compenso solo quando confermi di aver ricevuto il prodotto in buone condizioni.'
      },
      {
        question: 'Cosa succede se il prodotto non mi piace o è difettoso?',
        answer: 'Se il prodotto non corrisponde alla descrizione o presenta difetti, puoi aprire una contestazione prima di confermare la ricezione. Il nostro team valuterà il caso e, se necessario, procederà al rimborso totale o parziale.'
      },
      {
        question: 'Posso annullare un ordine dopo il pagamento?',
        answer: 'Per i prodotti a catalogo già pronti, puoi richiedere l\'annullamento prima della spedizione. Per i prodotti personalizzati o su misura, una volta iniziata la lavorazione non è possibile annullare (il diritto di recesso non si applica ai prodotti personalizzati).'
      },
      {
        question: 'Posso acquistare da più artigiani insieme?',
        answer: 'Sì! Puoi aggiungere prodotti di diversi artigiani al carrello e pagare tutto insieme. Ogni artigiano spedirà separatamente la propria parte dell\'ordine, e i costi di spedizione sono calcolati per ciascuno.'
      },
      {
        question: 'Come faccio a sapere se un artigiano è affidabile?',
        answer: 'Tutti gli artigiani su Rarifex sono verificati. Puoi vedere le recensioni dei clienti precedenti, il punteggio medio, il numero di ordini completati e da quanto tempo sono sulla piattaforma. Gli artigiani con partita IVA sono identificati con un badge.'
      },
      {
        question: 'Quanto tempo ho per confermare la ricezione?',
        answer: 'Hai 7 giorni dalla consegna per confermare la ricezione o aprire una contestazione. Se non fai nulla entro questo periodo, il sistema conferma automaticamente la ricezione e rilascia il pagamento all\'artigiano.'
      }
    ]
  },
  {
    id: 'pagamenti',
    name: 'Pagamenti',
    icon: CreditCard,
    color: 'bg-green-500',
    lightColor: 'bg-green-50',
    textColor: 'text-green-600',
    link: '/pagamenti',
    questions: [
      {
        question: 'Rarifex memorizza i dati della mia carta?',
        answer: 'No, assolutamente. Rarifex non vede mai, non riceve mai e non memorizza mai i dati della tua carta di credito o debito. Quando effettui un pagamento, vieni reindirizzato direttamente alla pagina sicura di Stripe. I tuoi dati di pagamento viaggiano direttamente dal tuo browser a Stripe, senza mai passare dai server di Rarifex.'
      },
      {
        question: 'Cos\'è Stripe e perché dovrei fidarmi?',
        answer: 'Stripe è una delle più grandi piattaforme di pagamento al mondo, fondata nel 2010 e valutata oltre 50 miliardi di dollari. Gestisce i pagamenti per aziende come Amazon, Google, Shopify, Booking.com, Deliveroo e milioni di altre. È certificata PCI DSS Level 1, il massimo livello di sicurezza nel settore dei pagamenti.'
      },
      {
        question: 'Cosa succede se devo essere rimborsato?',
        answer: 'I rimborsi vengono elaborati attraverso Stripe e riaccreditati sulla stessa carta usata per il pagamento. I tempi di riaccredito dipendono dalla tua banca, generalmente 5-10 giorni lavorativi per carte di credito, 2-5 giorni per carte di debito.'
      },
      {
        question: 'Posso pagare con PayPal?',
        answer: 'Al momento accettiamo pagamenti con carte di credito/debito, Apple Pay, Google Pay e bonifico SEPA. PayPal non è attualmente supportato, ma stiamo valutando di aggiungerlo in futuro.'
      },
      {
        question: 'Cosa significa "3D Secure"?',
        answer: '3D Secure è un ulteriore livello di sicurezza che richiede una verifica aggiuntiva (codice SMS, app della banca, impronta digitale) per autorizzare il pagamento. Protegge da utilizzi fraudolenti della carta anche se qualcuno ne conosce i dati.'
      },
      {
        question: 'Posso salvare la mia carta per acquisti futuri?',
        answer: 'Se crei un account su Rarifex, Stripe può memorizzare in modo sicuro un "token" della tua carta per velocizzare i pagamenti futuri. Anche in questo caso, i dati reali della carta restano esclusivamente su Stripe, mai su Rarifex.'
      }
    ]
  },
  {
    id: 'spedizioni',
    name: 'Spedizioni',
    icon: Truck,
    color: 'bg-amber-500',
    lightColor: 'bg-amber-50',
    textColor: 'text-amber-600',
    link: '/spedizioni',
    questions: [
      {
        question: 'Quanto costa la spedizione?',
        answer: 'I costi di spedizione variano in base all\'artigiano, al peso/dimensioni del prodotto e alla destinazione. Puoi vedere il costo esatto nel carrello prima di procedere al pagamento. Molti artigiani offrono la spedizione gratuita sopra una certa soglia di spesa.'
      },
      {
        question: 'Quanto tempo ci vuole per ricevere l\'ordine?',
        answer: 'I tempi dipendono da due fattori: il tempo di preparazione dell\'artigiano (indicato nella scheda prodotto) e i tempi del corriere. Per prodotti già pronti, la spedizione avviene in 1-3 giorni. Per prodotti su misura, i tempi sono indicati nella proposta accettata. La consegna in Italia richiede generalmente 2-5 giorni lavorativi.'
      },
      {
        question: 'Come posso tracciare il mio ordine?',
        answer: 'Quando l\'artigiano spedisce il pacco, ricevi una email con il codice di tracking e il link al sito del corriere. Puoi anche seguire lo stato dell\'ordine nella sezione "I miei ordini" della tua dashboard.'
      },
      {
        question: 'Spedite anche all\'estero?',
        answer: 'Dipende dall\'artigiano. Molti spediscono in tutta Europa, alcuni anche nel resto del mondo. Nella scheda prodotto e nel profilo dell\'artigiano puoi verificare le destinazioni disponibili. I costi e i tempi per l\'estero sono generalmente più elevati.'
      },
      {
        question: 'Cosa succede se non sono a casa al momento della consegna?',
        answer: 'Il corriere lascia un avviso e ritenta la consegna il giorno successivo. In alternativa, puoi ritirare il pacco presso il punto di ritiro indicato nell\'avviso. Alcuni corrieri permettono di riprogrammare la consegna online.'
      },
      {
        question: 'Posso far spedire a un indirizzo diverso dal mio?',
        answer: 'Certamente! Durante il checkout puoi indicare qualsiasi indirizzo di spedizione. È perfetto per i regali: puoi far recapitare il pacco direttamente al destinatario.'
      },
      {
        question: 'Il pacco arriverà ben imballato?',
        answer: 'Gli artigiani su Rarifex hanno a cuore i loro prodotti e li imballano con la massima cura. Utilizzano materiali protettivi adeguati al tipo di prodotto. Se hai esigenze particolari (es. confezione regalo), puoi chiederlo direttamente all\'artigiano via messaggio.'
      },
      {
        question: 'Cosa faccio se il pacco arriva danneggiato?',
        answer: 'Se il pacco presenta danni evidenti, fotografa l\'imballaggio e il prodotto prima di accettare la consegna o immediatamente dopo. Contatta l\'artigiano tramite la chat della piattaforma allegando le foto. Non confermare la ricezione finché il problema non è risolto: il pagamento all\'artigiano è trattenuto fino alla tua conferma.'
      },
      {
        question: 'Come funziona se ordino da più artigiani?',
        answer: 'Ogni artigiano spedisce separatamente, quindi riceverai più pacchi (uno per artigiano). Ogni pacco ha il suo tracking e le spese di spedizione sono calcolate separatamente. Se ordini più prodotti dallo stesso artigiano, la spedizione viene combinata.'
      }
    ]
  },
  {
    id: 'resi',
    name: 'Resi e Rimborsi',
    icon: RotateCcw,
    color: 'bg-teal-500',
    lightColor: 'bg-teal-50',
    textColor: 'text-teal-600',
    link: '/resi-rimborsi',
    questions: [
      {
        question: 'Posso restituire un prodotto personalizzato?',
        answer: 'No, i prodotti realizzati su misura o personalizzati secondo le tue indicazioni non possono essere restituiti per ripensamento. Questo è previsto dall\'Art. 59 del Codice del Consumo. Tuttavia, se il prodotto è difettoso o non conforme a quanto concordato, hai comunque diritto alla sostituzione o al rimborso.'
      },
      {
        question: 'Entro quanto tempo posso chiedere il reso?',
        answer: 'Per i prodotti standard (non personalizzati), hai 14 giorni dalla ricezione per esercitare il diritto di recesso senza dover fornire motivazioni. Per segnalare difetti o non conformità, hai tempo fino a quando non confermi la ricezione (massimo 7 giorni dalla consegna).'
      },
      {
        question: 'Chi paga le spese di spedizione per il reso?',
        answer: 'Dipende dal motivo del reso. Se il prodotto è difettoso o non conforme, le spese sono a carico dell\'artigiano. Se eserciti il diritto di recesso per ripensamento (prodotto conforme), le spese di reso sono generalmente a tuo carico, salvo diversi accordi con l\'artigiano.'
      },
      {
        question: 'Quanto tempo ci vuole per ricevere il rimborso?',
        answer: 'Una volta approvato il rimborso, Stripe lo elabora in 1-3 giorni lavorativi. La tua banca poi riaccredita l\'importo sulla carta in 5-10 giorni lavorativi. I tempi totali sono quindi di circa 1-2 settimane, ma possono variare in base alla tua banca.'
      },
      {
        question: 'Posso cambiare un prodotto con un altro?',
        answer: 'Gli scambi diretti non sono previsti dalla piattaforma. Puoi richiedere il reso e il rimborso del prodotto originale, e poi effettuare un nuovo ordine per il prodotto desiderato. Alcuni artigiani potrebbero essere disponibili a gestire scambi diretti: contattali via chat per verificare.'
      },
      {
        question: 'E se l\'artigiano non risponde?',
        answer: 'Se l\'artigiano non risponde entro 48 ore, contatta il supporto Rarifex. Interveniamo come mediatori e, se necessario, possiamo procedere al rimborso diretto trattenendo i fondi che non sono ancora stati rilasciati all\'artigiano.'
      },
      {
        question: 'Come funziona la garanzia legale?',
        answer: 'Tutti i prodotti sono coperti dalla garanzia legale di 2 anni per difetti di conformità (Art. 128-135 Codice del Consumo). Se un difetto si manifesta entro 2 anni dalla consegna, puoi richiedere la riparazione o sostituzione gratuita, e se non possibile, la riduzione del prezzo o il rimborso.'
      },
      {
        question: 'Cosa succede se ho già confermato la ricezione?',
        answer: 'Se hai già confermato la ricezione, il pagamento è stato rilasciato all\'artigiano. Puoi comunque contattare l\'artigiano per segnalare il problema e cercare una soluzione. Per difetti non evidenti scoperti successivamente, la garanzia legale ti tutela per 2 anni.'
      }
    ]
  },
  {
    id: 'personalizzati',
    name: 'Prodotti su Misura',
    icon: Paintbrush,
    color: 'bg-purple-500',
    lightColor: 'bg-purple-50',
    textColor: 'text-purple-600',
    link: '/prodotto-su-misura',
    questions: [
      {
        question: 'Quanto costa un prodotto su misura?',
        answer: 'I prezzi variano enormemente in base al tipo di prodotto, ai materiali, alla complessità e al tempo richiesto. Puoi indicare il tuo budget nella richiesta e gli artigiani ti proporranno soluzioni compatibili. Riceverai preventivi dettagliati prima di impegnarti.'
      },
      {
        question: 'Quanto tempo ci vuole per realizzare un prodotto su misura?',
        answer: 'Dipende dal tipo di prodotto e dalla complessità. Un gioiello semplice può richiedere 1-2 settimane, un mobile su misura anche 2-3 mesi. Ogni proposta include i tempi stimati, e puoi indicare se hai una scadenza specifica.'
      },
      {
        question: 'E se il risultato non mi piace?',
        answer: 'Durante la lavorazione ricevi aggiornamenti e puoi dare feedback. Se alla fine il prodotto non corrisponde a quanto concordato, puoi aprire una contestazione prima di confermare la ricezione. Il pagamento è protetto fino alla tua conferma.'
      },
      {
        question: 'Posso chiedere modifiche durante la lavorazione?',
        answer: 'Piccole modifiche sono spesso possibili nelle fasi iniziali. Cambiamenti sostanziali potrebbero comportare costi aggiuntivi o allungamento dei tempi. L\'importante è comunicare tempestivamente con l\'artigiano.'
      },
      {
        question: 'Come faccio a spiegare bene cosa voglio?',
        answer: 'Non preoccuparti se non hai le idee chiare al 100%. Descrivi l\'utilizzo, lo stile che ti piace, allega immagini di ispirazione. Gli artigiani sono abituati a guidare i clienti nella definizione del progetto.'
      },
      {
        question: 'Posso richiedere una copia di un prodotto esistente?',
        answer: 'Puoi usare immagini come ispirazione, ma gli artigiani creeranno un pezzo originale, non una copia esatta. Questo garantisce l\'unicità e rispetta la proprietà intellettuale di altri designer.'
      },
      {
        question: 'Ho diritto al recesso sui prodotti su misura?',
        answer: 'Per i prodotti realizzati su misura o personalizzati, il diritto di recesso non si applica (Art. 59 Codice del Consumo). Per questo è importante definire bene i dettagli prima di confermare. La garanzia legale sui difetti rimane comunque attiva.'
      },
      {
        question: 'Posso incontrare l\'artigiano di persona?',
        answer: 'Dipende dall\'artigiano e dalla sua disponibilità. Alcuni accolgono visite in bottega, altri lavorano solo da remoto. Puoi sempre chiedere tramite la chat della piattaforma.'
      }
    ]
  }
];

export default function FAQPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);

  // Filter FAQs based on search query
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) {
      return activeCategory 
        ? FAQ_CATEGORIES.filter(cat => cat.id === activeCategory)
        : FAQ_CATEGORIES;
    }

    const query = searchQuery.toLowerCase();
    return FAQ_CATEGORIES.map(category => ({
      ...category,
      questions: category.questions.filter(
        q => q.question.toLowerCase().includes(query) || 
             q.answer.toLowerCase().includes(query)
      )
    })).filter(category => category.questions.length > 0);
  }, [searchQuery, activeCategory]);

  // Count total results
  const totalResults = useMemo(() => {
    return filteredCategories.reduce((acc, cat) => acc + cat.questions.length, 0);
  }, [filteredCategories]);

  const toggleItem = (categoryId, questionIndex) => {
    const key = `${categoryId}-${questionIndex}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isExpanded = (categoryId, questionIndex) => {
    return expandedItems[`${categoryId}-${questionIndex}`] || false;
  };

  return (
    <>
      <Helmet>
        <title>FAQ - Domande Frequenti | Rarifex</title>
        <meta name="description" content="Trova risposte alle domande più frequenti su Rarifex: acquisti, pagamenti, spedizioni, resi, prodotti su misura e molto altro. Tutto quello che devi sapere sul marketplace dell'artigianato italiano." />
        <meta name="keywords" content="faq rarifex, domande frequenti, aiuto rarifex, supporto, acquisti, pagamenti, spedizioni, resi, prodotti personalizzati" />
        <link rel="canonical" href="https://www.rarifex.com/faq" />
        <meta property="og:title" content="FAQ - Domande Frequenti | Rarifex" />
        <meta property="og:description" content="Risposte alle domande più frequenti su acquisti, pagamenti, spedizioni e resi su Rarifex." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
        
        {/* HERO SECTION */}
        <section className="relative py-12 md:py-20 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-shop-primary/10 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-shop-primary/10 text-shop-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <HelpCircle className="w-4 h-4" />
                Centro Assistenza
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
                Domande Frequenti
              </h1>
              
              <p className="text-lg text-slate-600 mb-8">
                Trova rapidamente le risposte alle tue domande su acquisti, pagamenti, spedizioni e molto altro.
              </p>

              {/* Search Bar */}
              <div className="relative max-w-xl mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Cerca una domanda..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50 focus:outline-none focus:ring-2 focus:ring-shop-primary/20 focus:border-shop-primary text-slate-900 placeholder:text-slate-400"
                />
                {searchQuery && (
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-500">
                    {totalResults} risultat{totalResults === 1 ? 'o' : 'i'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* CATEGORY FILTER */}
        <section className="py-6 border-b border-slate-100 sticky top-0 bg-white/95 backdrop-blur-sm z-20">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={() => setActiveCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === null
                    ? 'bg-shop-primary text-white shadow-md'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Tutte le categorie
              </button>
              {FAQ_CATEGORIES.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(activeCategory === category.id ? null : category.id)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      activeCategory === category.id
                        ? `${category.color} text-white shadow-md`
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {category.name}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ CONTENT */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              
              {filteredCategories.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    Nessun risultato trovato
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Prova con termini di ricerca diversi o contattaci direttamente.
                  </p>
                  <Link
                    to="/contatti"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-shop-primary text-white rounded-xl font-medium hover:bg-shop-primary-dark transition-colors"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Contattaci
                  </Link>
                </div>
              ) : (
                <div className="space-y-10">
                  {filteredCategories.map((category) => {
                    const Icon = category.icon;
                    return (
                      <div key={category.id}>
                        {/* Category Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 ${category.lightColor} ${category.textColor} rounded-xl flex items-center justify-center`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div>
                              <h2 className="text-xl font-bold text-slate-900">{category.name}</h2>
                              <p className="text-sm text-slate-500">{category.questions.length} domande</p>
                            </div>
                          </div>
                          <Link
                            to={category.link}
                            className={`text-sm font-medium ${category.textColor} hover:underline hidden sm:block`}
                          >
                            Guida completa →
                          </Link>
                        </div>

                        {/* Questions */}
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden divide-y divide-slate-100">
                          {category.questions.map((faq, index) => (
                            <div key={index}>
                              <button
                                onClick={() => toggleItem(category.id, index)}
                                className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50/50 transition-colors"
                              >
                                <span className="font-medium text-slate-900 pr-4">{faq.question}</span>
                                <ChevronDown 
                                  className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${
                                    isExpanded(category.id, index) ? 'rotate-180' : ''
                                  }`}
                                />
                              </button>
                              <div 
                                className={`overflow-hidden transition-all duration-200 ${
                                  isExpanded(category.id, index) ? 'max-h-96' : 'max-h-0'
                                }`}
                              >
                                <p className="px-5 pb-5 text-slate-600 leading-relaxed">
                                  {faq.answer}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Mobile link */}
                        <Link
                          to={category.link}
                          className={`mt-3 inline-flex items-center text-sm font-medium ${category.textColor} hover:underline sm:hidden`}
                        >
                          Guida completa →
                        </Link>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="py-16 bg-gradient-to-br from-shop-primary to-shop-primary-dark">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center text-white">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Non hai trovato la risposta?
              </h2>
              <p className="text-white/80 mb-8">
                Il nostro team è pronto ad aiutarti. Contattaci e ti risponderemo il prima possibile.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contatti"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-shop-primary rounded-xl font-semibold hover:bg-slate-100 transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  Contattaci
                </Link>
                <Link
                  to="/assistenza"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white border border-white/20 rounded-xl font-semibold hover:bg-white/20 transition-colors"
                >
                  <HelpCircle className="w-5 h-5" />
                  Centro Assistenza
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
